import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  styled,
  Snackbar,
  Alert,
} from '@mui/material';
import { useDataProvider } from 'react-admin';

const StyledTextField = styled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  color: 'black',
  '& .MuiOutlinedInput-root': {
    '& fieldset': { borderColor: 'black' },
  },
  '& label': { color: 'black' },
  '& input': { color: 'black' },
}));

function validateEmail(email: string): boolean {
  // Simple email regex
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

function validateUSPhone(phone: string): boolean {
  // US phone number regex: allows optional +1 or 1 region code, spaces, dashes, dots, parentheses
  return /^(\+?1\s?)?(\([2-9]\d{2}\)|[2-9]\d{2})[-.\s]?\d{3}[-.\s]?\d{4}$/.test(phone);
}

export const FeedbackForm = () => {
  const dataProvider = useDataProvider();
  
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; success: boolean; error?: string }>(
    { open: false, success: false }
  );

  const emailError = email.length > 0 && !validateEmail(email);
  const phoneError = phone.length > 0 && !validateUSPhone(phone);

  const isSendDisabled =
    !email ||
    !message ||
    emailError ||
    (phone.length > 0 && phoneError) ||
    sending;

  const handleSend = async () => {
    setSending(true);
    try {

      const response = await dataProvider.feedback({
        firstName,
        lastName,
        email,
        phone,
        message,
      });

      if (response.ok) {
        setSnackbar({ open: true, success: true });
        setFirstName('');
        setLastName('');
        setEmail('');
        setPhone('');
        setMessage('');
      } else {
        const error = await response.text();
        setSnackbar({ open: true, success: false, error });
      }
    } catch (err: any) {
      setSnackbar({ open: true, success: false, error: err.message });
    } finally {
      setSending(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', color: 'black' }}>
      <Typography variant="h5" sx={{ mb: 2 }}>
        Connect With Us Today
      </Typography>
      <StyledTextField
        label="First Name"
        fullWidth
        variant="outlined"
        value={firstName}
        onChange={e => setFirstName(e.target.value)}
        autoComplete="given-name"
      />
      <StyledTextField
        label="Last Name"
        fullWidth
        variant="outlined"
        value={lastName}
        onChange={e => setLastName(e.target.value)}
        autoComplete="family-name"
      />
      <StyledTextField
        label="Email"
        fullWidth
        type='email'
        name='email'
        variant="outlined"
        required
        value={email}
        onChange={e => setEmail(e.target.value)}
        error={emailError}
      />
      <StyledTextField
        label="Phone"
        fullWidth
        variant="outlined"
        value={phone}
        onChange={e => setPhone(e.target.value)}
        error={phoneError}
        autoComplete="tel"
      />
      <StyledTextField
        label="Tell us what you need help with..."
        variant="outlined"
        fullWidth
        multiline
        rows={5}
        required
        value={message}
        onChange={e => setMessage(e.target.value)}
        slotProps={{
          input: { style: { color: 'black' } }
        }}
      />
      <Button
        variant="outlined"
        sx={{
          //width: '75%',
          //alignSelf: 'center',
          borderColor: 'white',
          color: 'white',
          backgroundColor: 'black',
          textTransform: 'none',
          '&:hover': { color: 'black', backgroundColor: 'white', borderColor: 'black', },
          '&.Mui-disabled': {
            backgroundColor: 'black',
            color: 'white',
            borderColor: 'black',
          },
        }}
        disabled={isSendDisabled}
        onClick={handleSend}
      >
        {sending ? 'Sending...' : 'Send'}
      </Button>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ open: false, success: snackbar.success })}
      >
        <Alert
          onClose={() => setSnackbar({ open: false, success: snackbar.success })}
          severity={snackbar.success ? 'success' : 'error'}
          sx={{ width: '100%' }}
        >
          {snackbar.success
            ? 'Feedback sent successfully!'
            : `Failed to send feedback${snackbar.error ? `: ${snackbar.error}` : ''}`}
        </Alert>
      </Snackbar>
    </Box>
  );
};
